import InvitationClient from 'clients/cooperation/invitation_client'
import { FormErrorTextV2 } from 'components/form_error_text'
import * as React from 'react'
import { useState } from 'react'
import styles from './invitation_form.module.scss'
import * as classnames from 'classnames/bind'
import Company from 'entities/company'
import Group from 'entities/group'
import Offer from 'entities/offer'
import Recruiter from 'entities/recruiter'
import { OpenInvitationModalPropsContext } from './open_invitation_modal_button'
import { Formik, FormikActions, FormikProps } from 'formik'
import { trim } from 'lodash'
import * as Yup from 'yup'
import Toast from 'components/toast'
import Invitation from 'entities/cooperation/invitation'
import RecruiterSelector from './recruiter_selector'
import RecruiterClient from 'clients/recruiter_client'
import { FormField } from './form_field'

const cx = classnames.bind(styles)

type Props = {
  onComplete: (invitation: Invitation) => void
}

type FormValues = {
  introducer: Recruiter
  candidateName: string
}

const validationSchema = Yup.object().shape<FormValues>({
  introducer: Yup.object().required(),
  candidateName: Yup.string().required()
})

export const InvitationForm: React.FC<Props> = ({ onComplete }) => {
  const [apiErrors, setApiErrors] = useState(null)
  const [recruiterSelectorKeyword, setRecruiterSelectorKeyword] = useState('')
  const [fetchedRecruiters, setFetchedRecruiters] = useState([])

  const handleSubmit = (
    values: FormValues,
    actions: FormikActions<FormValues>,
    company: Company,
    offer: Offer,
    group: Group,
    contentId: number
  ) => {
    const data = {
      token_company: company.token,
      token_offer: offer.token,
      token_group: group.token,
      token_recruiter: values.introducer.token,
      cooperation_invitation: {
        name: values.candidateName
      },
      content_id: contentId
    }

    InvitationClient.createInvitation(data)
      .then(invitation => {
        actions.setSubmitting(false)
        onComplete(invitation)
      })
      .catch(({ response }) => {
        Toast.notify(I18n.t('offers.pages.show.components.invitation_form.message.publish_url.failure'))
        setApiErrors(response.data.errors)
        actions.setSubmitting(false)
      })
  }

  const handleChangeKeywordForRecruiter = (
    keyword: string,
    companyToken: string,
    offerToken: string,
    groupToken: string
  ) => {
    setRecruiterSelectorKeyword(keyword)
    if (keyword === '') {
      setFetchedRecruiters([])
    } else {
      RecruiterClient.searchIntroducers(keyword, companyToken, offerToken, groupToken).then(recruiters => {
        setFetchedRecruiters(recruiters)
      })
    }
  }

  return (
    <OpenInvitationModalPropsContext.Consumer>
      {({ introducer, company, contentId, offer, group, searchGroup, previewUrl, isPreview }) => (
        <Formik<FormValues>
          initialValues={{ introducer: introducer, candidateName: '' }}
          onSubmit={(values, actions) => handleSubmit(values, actions, company, offer, group, contentId)}
          validationSchema={validationSchema}
          render={({ setFieldValue, handleSubmit, values, isSubmitting, isValid }: FormikProps<FormValues>) => (
            <form onSubmit={handleSubmit}>
              <div className={styles.formContainer}>
                <div className={styles.formGroup}>
                  <label htmlFor="recruiter_name" className={styles.formGroupLabel}>
                    {I18n.t('offers.pages.show.components.invitation_form.recruiter.label')}
                  </label>
                  <RecruiterSelector
                    keyword={recruiterSelectorKeyword}
                    recruiters={fetchedRecruiters}
                    selectedRecruiter={values.introducer}
                    onChangeKeywordForRecruiter={keyword =>
                      handleChangeKeywordForRecruiter(keyword, company.token, offer.token, searchGroup.token)
                    }
                    onSelectRecruiter={newIntroducer => setFieldValue('introducer', newIntroducer, true)}
                    onDeselectRecruiter={() => {
                      setFetchedRecruiters([])
                      setFieldValue('introducer', null, true)
                    }}
                    selectable={offer.introducerSelectable && !isPreview}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="candidate_name" className={styles.formGroupLabel}>
                    {I18n.t('offers.pages.show.components.invitation_form.candidate.label')}
                  </label>
                  <div className={styles.formGroupContent}>
                    <div className={styles.candidateContainer}>
                      <div className={styles.candidate}>
                        <FormField
                          id="candidateName"
                          isActive={values.candidateName !== ''}
                          label={I18n.t('offers.pages.show.components.invitation_form.candidate.placeholder')}
                          name="candidateName"
                          required
                          value={values.candidateName}
                          readOnly={false}
                          onChange={(name: string) => setFieldValue('candidateName', trim(name), true)}
                        />
                      </div>
                    </div>
                    <FormErrorTextV2 errors={apiErrors} name="name" />
                  </div>
                </div>
              </div>
              <div className={styles.actions}>
                <button
                  type="submit"
                  disabled={isPreview || isSubmitting || !isValid}
                  className={cx(styles.button, styles.buttonSubmit)}
                >
                  {isSubmitting
                    ? I18n.t(
                        'offers.pages.show.components.invitation_form.message.publish_invitation_page_url.creating'
                      )
                    : I18n.t('offers.pages.show.components.invitation_form.message.publish_invitation_page_url.create')}
                </button>
                <a className={styles.link} target="_blank" href={previewUrl} rel="noopener noreferrer">
                  {I18n.t('offers.pages.show.components.invitation_form.page_confirm')}
                </a>
              </div>
            </form>
          )}
        />
      )}
    </OpenInvitationModalPropsContext.Consumer>
  )
}
